<script setup lang="ts">
/**
 * SectionTestimonials
 *
 * Displays customer testimonials.
 * Can fetch from API or use embedded items.
 * Desktop: Carousel showing 3 at a time. Mobile: Carousel showing 1 at a time.
 *
 * Interaction: "Look at this" (read-only content)
 * - No lift (implies link to case study)
 * - Shadow deepen on hover for subtle depth
 */

import type { SectionTestimonialsProps, TestimonialItem } from "~~/shared/types";
import type { CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

const props = withDefaults(defineProps<SectionTestimonialsProps>(), {
  title: "What our clients say",
  sectionBg: "white",
});

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

// Fetch from API during SSR when no embedded items are provided.
// Running this in useAsyncData (not onMounted) ensures testimonials are
// present in the server-rendered HTML, so crawlers see real social proof
// instead of an empty "No testimonials available" state.
const embeddedItems = props.items;
const hasEmbeddedItems = !!embeddedItems && embeddedItems.length > 0;

const { data: fetchedTestimonials } = await useAsyncData<TestimonialItem[]>(
  "testimonials-default",
  async () => {
    if (hasEmbeddedItems) {
      return embeddedItems;
    }
    try {
      const response = await $fetch<{ success: boolean; data: TestimonialItem[] }>(
        "/api/testimonials"
      );
      return response.success && response.data ? response.data : [];
    } catch (error) {
      console.error("Failed to fetch testimonials:", error);
      return [];
    }
  },
  { default: () => [] }
);

const testimonials = computed<TestimonialItem[]>(() => fetchedTestimonials.value ?? []);
const hasTestimonials = computed(() => testimonials.value.length > 0);

// Desktop carousel state
const desktopApi = ref<CarouselApi>();
const desktopCurrent = ref(0);
const desktopCount = ref(0);

// Mobile carousel state
const mobileApi = ref<CarouselApi>();
const mobileCurrent = ref(0);
const mobileCount = ref(0);

function setDesktopApi(api: CarouselApi) {
  desktopApi.value = api;
}

function setMobileApi(api: CarouselApi) {
  mobileApi.value = api;
}

watch(desktopApi, (api) => {
  if (!api) return;

  desktopCount.value = api.scrollSnapList().length;
  desktopCurrent.value = api.selectedScrollSnap();

  api.on("select", () => {
    desktopCurrent.value = api.selectedScrollSnap();
  });
});

watch(mobileApi, (api) => {
  if (!api) return;

  mobileCount.value = api.scrollSnapList().length;
  mobileCurrent.value = api.selectedScrollSnap();

  api.on("select", () => {
    mobileCurrent.value = api.selectedScrollSnap();
  });
});

function scrollDesktopTo(index: number) {
  desktopApi.value?.scrollTo(index);
}

function scrollMobileTo(index: number) {
  mobileApi.value?.scrollTo(index);
}
</script>

<template>
  <section v-if="hasTestimonials" :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Header -->
      <div class="mb-12 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ title }}
        </h2>
      </div>

      <!-- Desktop: Carousel showing 3 at a time -->
        <div class="hidden lg:block">
          <ClientOnly>
            <Carousel
              class="w-full"
              :opts="{
                align: 'start',
                loop: testimonials.length > 3,
              }"
              @init-api="setDesktopApi"
            >
              <CarouselContent class="-ml-4">
                <CarouselItem
                  v-for="(testimonial, index) in testimonials"
                  :key="index"
                  class="basis-1/3 pl-4"
                >
                  <Card
                    class="flex h-full flex-col rounded-2xl border-neutral-200 shadow-sm transition-shadow duration-300 hover:shadow-md"
                  >
                    <CardContent class="flex flex-1 flex-col p-8">
                      <!-- Quote Icon -->
                      <Icon name="lucide:quote" size="32" class="mb-4 text-mw-accent-300" />

                      <!-- Quote Text -->
                      <p class="mb-6 flex-1 text-lg leading-relaxed text-neutral-700">
                        "{{ testimonial.quote }}"
                      </p>

                      <!-- Author (pushed to bottom with mt-auto) -->
                      <div class="mt-auto flex items-center gap-3">
                        <img
                          v-if="testimonial.authorAvatar"
                          :src="testimonial.authorAvatar"
                          :alt="testimonial.authorName"
                          class="size-12 shrink-0 rounded-full object-cover"
                        />
                        <div
                          v-else
                          class="flex size-12 shrink-0 items-center justify-center rounded-full bg-mw-accent-100 font-bold text-mw-accent-600"
                        >
                          {{ testimonial.authorName.charAt(0) }}
                        </div>

                        <div class="flex h-12 flex-col justify-center">
                          <span class="block font-semibold leading-tight text-slate-900">
                            {{ testimonial.authorName }}
                          </span>
                          <span class="block mt-0.5 text-sm leading-snug text-neutral-500">
                            {{ testimonial.authorRole
                            }}<span v-if="testimonial.company">, {{ testimonial.company }}</span>
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              </CarouselContent>

              <!-- Navigation arrows - only show if more than 3 testimonials -->
              <template v-if="testimonials.length > 3">
                <CarouselPrevious class="left-0 -translate-x-1/2" />
                <CarouselNext class="right-0 translate-x-1/2" />
              </template>
            </Carousel>

            <!-- Dot navigation - only show if more than 3 testimonials -->
            <div v-if="desktopCount > 1" class="mt-6 flex justify-center gap-2">
              <button
                v-for="index in desktopCount"
                :key="index"
                :class="[
                  'size-2.5 rounded-full transition-colors duration-200',
                  desktopCurrent === index - 1
                    ? 'bg-primary-500'
                    : 'bg-neutral-300 hover:bg-neutral-400',
                ]"
                :aria-label="`Go to testimonial group ${index}`"
                @click="scrollDesktopTo(index - 1)"
              ></button>
            </div>

            <!-- SSR Fallback: Show first 3 as static grid -->
            <template #fallback>
              <div class="grid grid-cols-3 gap-4">
                <Card
                  v-for="(testimonial, index) in testimonials.slice(0, 3)"
                  :key="index"
                  class="flex h-full flex-col rounded-2xl border-neutral-200 shadow-sm"
                >
                  <CardContent class="flex flex-1 flex-col p-8">
                    <Icon name="lucide:quote" size="32" class="mb-4 text-mw-accent-300" />
                    <p class="mb-6 flex-1 text-lg leading-relaxed text-neutral-700">
                      "{{ testimonial.quote }}"
                    </p>
                    <div class="mt-auto flex items-center gap-3">
                      <div class="flex size-12 shrink-0 items-center justify-center rounded-full bg-mw-accent-100 font-bold text-mw-accent-600">
                        {{ testimonial.authorName.charAt(0) }}
                      </div>
                      <div class="flex h-12 flex-col justify-center">
                        <span class="block font-semibold leading-tight text-slate-900">{{ testimonial.authorName }}</span>
                        <span class="block mt-0.5 text-sm leading-snug text-neutral-500">
                          {{ testimonial.authorRole }}<span v-if="testimonial.company">, {{ testimonial.company }}</span>
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </template>
          </ClientOnly>
        </div>

        <!-- Mobile: Carousel showing 1 at a time -->
        <div class="lg:hidden">
          <ClientOnly>
            <Carousel class="w-full" @init-api="setMobileApi">
              <CarouselContent>
                <CarouselItem v-for="(testimonial, index) in testimonials" :key="index">
                  <Card class="rounded-2xl border-neutral-200 shadow-sm">
                    <CardContent class="p-8">
                      <!-- Quote Icon -->
                      <Icon name="lucide:quote" size="32" class="mb-4 text-mw-accent-300" />

                      <!-- Quote Text -->
                      <p class="mb-6 text-lg leading-relaxed text-neutral-700">
                        "{{ testimonial.quote }}"
                      </p>

                      <!-- Author -->
                      <div class="flex items-center gap-3">
                        <img
                          v-if="testimonial.authorAvatar"
                          :src="testimonial.authorAvatar"
                          :alt="testimonial.authorName"
                          class="size-12 shrink-0 rounded-full object-cover"
                        />
                        <div
                          v-else
                          class="flex size-12 shrink-0 items-center justify-center rounded-full bg-mw-accent-100 font-bold text-mw-accent-600"
                        >
                          {{ testimonial.authorName.charAt(0) }}
                        </div>

                        <div class="flex h-12 flex-col justify-center">
                          <span class="block font-semibold leading-tight text-slate-900">
                            {{ testimonial.authorName }}
                          </span>
                          <span class="block mt-0.5 text-sm leading-snug text-neutral-500">
                            {{ testimonial.authorRole
                            }}<span v-if="testimonial.company"
                              >, {{ testimonial.company }}</span
                            >
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              </CarouselContent>

              <!-- Navigation with arrows and dots -->
              <div class="mt-6 flex items-center justify-center gap-4">
                <CarouselPrevious class="static translate-y-0" />

                <!-- Dot navigation for mobile -->
                <div v-if="mobileCount > 1" class="flex gap-2">
                  <button
                    v-for="index in mobileCount"
                    :key="index"
                    :class="[
                      'size-2.5 rounded-full transition-colors duration-200',
                      mobileCurrent === index - 1
                        ? 'bg-primary-500'
                        : 'bg-neutral-300 hover:bg-neutral-400',
                    ]"
                    :aria-label="`Go to testimonial ${index}`"
                    @click="scrollMobileTo(index - 1)"
                  ></button>
                </div>

                <CarouselNext class="static translate-y-0" />
              </div>
            </Carousel>
            <template #fallback>
              <!-- Show first testimonial as static on SSR -->
              <Card v-if="testimonials.length > 0" class="rounded-2xl border-neutral-200 shadow-sm">
                <CardContent class="p-8">
                  <Icon name="lucide:quote" size="32" class="mb-4 text-mw-accent-300" />
                  <p class="mb-6 text-lg leading-relaxed text-neutral-700">
                    "{{ testimonials[0].quote }}"
                  </p>
                  <div class="flex items-center gap-3">
                    <img
                      v-if="testimonials[0].authorAvatar"
                      :src="testimonials[0].authorAvatar"
                      :alt="testimonials[0].authorName"
                      class="size-12 shrink-0 rounded-full object-cover"
                    />
                    <div
                      v-else
                      class="flex size-12 shrink-0 items-center justify-center rounded-full bg-mw-accent-100 font-bold text-mw-accent-600"
                    >
                      {{ testimonials[0].authorName.charAt(0) }}
                    </div>
                    <div class="flex h-12 flex-col justify-center">
                      <span class="block font-semibold leading-tight text-slate-900">{{ testimonials[0].authorName }}</span>
                      <span class="block mt-0.5 text-sm leading-snug text-neutral-500">
                        {{ testimonials[0].authorRole }}<span v-if="testimonials[0].company">, {{ testimonials[0].company }}</span>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </template>
          </ClientOnly>
        </div>
    </div>
  </section>
</template>
